// src/lib/theme.ts (T1): the tenant brand as CSS custom properties (spec 2.8, design system A, accent-led). A builder's
// brand is its logo and one accent; tokens.css declares every token with homebuild's own values (Onest, the warm greys,
// the shapes), and applyTheme() sets the accent's derived shades (shared/accent.ts) as --brand-accent-* on <html> at
// boot, sets <meta name="theme-color">, the installed app's meta tags (brand.app) and the document title. No tenant
// font loads: type is homebuild's (Onest, self-hosted, tokens.css).
import { accentProperties } from "../../shared/accent";
import type { PublicBrand } from "../../shared/tenant";

/** Brand token name to its value, in the order they are set. Pure, so it is unit tested. */
export function brandTokens(brand: Pick<PublicBrand, "accent">): [string, string][] {
  return accentProperties(brand.accent);
}

// A token value is a color: nothing that could end the declaration.
const SAFE_VALUE = /^#[0-9A-Fa-f]{6}$/;

/** Sets `<meta name={name} content={content}>`, adding the tag once. */
function setMeta(doc: Document, name: string, content: string): void {
  let meta = doc.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = doc.createElement("meta");
    meta.name = name;
    doc.head.appendChild(meta);
  }
  meta.content = content;
}

export type ThemeOptions = {
  title?: string;
  doc?: Document;
  /** Running as the installed app (src/lib/pwa.ts#inApp): the status bar takes brand.app.themeColor. */
  standalone?: boolean;
  /** PWA_MODE allows installing: the home-screen capable flags and the iOS status bar style. */
  installable?: boolean;
};

export function applyTheme(brand: PublicBrand, opts?: ThemeOptions): void {
  const doc = opts?.doc ?? document;
  const root = doc.documentElement;
  const tokens = brandTokens(brand);
  for (const [name, value] of tokens) {
    if (typeof value === "string" && SAFE_VALUE.test(value)) root.style.setProperty(name, value);
  }

  // The installed app's status bar matches its header (brand.app.themeColor, white today); a browser tab takes the
  // builder's accent (docs/DECISIONS.md, "Installable app").
  const app = brand.app;
  const accent = tokens[0]![1];
  setMeta(doc, "theme-color", app && opts?.standalone ? app.themeColor : accent);
  if (app) setMeta(doc, "apple-mobile-web-app-title", app.shortName);
  if (app && opts?.installable) {
    setMeta(doc, "mobile-web-app-capable", "yes");
    setMeta(doc, "apple-mobile-web-app-capable", "yes");
    setMeta(doc, "apple-mobile-web-app-status-bar-style", app.iosStatusBar ?? "default");
  }

  if (opts?.title) doc.title = opts.title;
}
